import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SettingsPage from './pages/SettingsPage';
import MenuPage from './pages/MenuPage';
import FlybyPage from './pages/FlybyPage';
import IncidentsPage from './pages/IncidentsPage';
import InstructionsPage from './pages/InstructionsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import { THEMES, ThemeProvider, useTheme } from './theme';
import { muteAircraft, unmuteAircraft } from './api';
import RadarMap from './RadarMap';

const API_BASE = process.env.REACT_APP_API_BASE || '';
// Adaptive poll cadence: each poll both sends our position and fetches the
// situation, so we slow down when there's nothing to react to.
const POLL_ALERT_MS = 3_000;    // alerts present → fast
const POLL_IDLE_MS  = 30_000;   // no alerts → slow

// Phone "device card" frame for desktop debugging. On a real device the UI stays
// full-screen; in a desktop window it's clamped to a centered iPhone-12-sized card
// (390×844 logical px) so it looks like the phone.
const PHONE_W = 390;
const PHONE_H = 844;

// The card is a desktop debugging aid, so it needs an actual desktop — not merely a
// wide viewport: modern phones are 393–430 logical px and would otherwise get a fake
// bezel drawn around them. `(hover: hover) and (pointer: fine)` means a real mouse or
// trackpad, which rules out phones, tablets and DevTools device emulation alike.
const FRAME_QUERY = `(min-width: ${PHONE_W + 1}px) and (hover: hover) and (pointer: fine)`;

// Installed as a PWA → definitely a real device. Can't change without a reload, so
// it's folded into the result rather than watched.
const IS_STANDALONE =
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

// Theme palette + provider live in ./theme (shared with the auth/settings pages).

function useShowPhoneFrame() {
  const [matches, setMatches] = useState(() => window.matchMedia(FRAME_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(FRAME_QUERY);
    const onChange = e => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return matches && !IS_STANDALONE;
}

const LEVEL = {
  CRITICAL: { bg: '#ff2222', dim: '#7a0000', label: 'CRITICAL' },
  WARNING:  { bg: '#ff8c00', dim: '#7a4000', label: 'WARNING' },
  INFO:     { bg: '#0099cc', dim: '#004466', label: 'INFO' },
};

const STATUS_COLOR = {
  CRITICAL: '#ff2222',
  WARNING:  '#ff8c00',
  INFO:     '#0099cc',
};

function bearingToCard(deg) {
  if (deg == null) return '—';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
}

// Stable identity for one contact, mirroring the backend's
// `dedup.canonical_key_from_parts`: icao24 wins, else the callsign stripped to
// alphanumerics. Used as the React key (a blank callsign would otherwise collide)
// and as the id sent with a mute request.
function acKey(ac) {
  if (!ac) return '';
  if (ac.icao24) return String(ac.icao24).toUpperCase();
  return String(ac.callsign || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

const FR24_BASE = 'https://www.flightradar24.com';

// FlightRadar24 deep link for a contact: `flightradar24.com/<CALLSIGN>` — the LIVE
// map focused on that aircraft, which is what you want while it's next to you.
// (Not /data/flights/<ident>, which is the flight's history page.) FR24 has no
// public lookup by Mode-S hex, so the callsign is all we have; when there isn't
// one, fall back to the live map at the contact's position so the button always
// lands somewhere useful.
function fr24UrlFor(ac) {
  const cs = String(ac.callsign || '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (cs) return `${FR24_BASE}/${cs}`;
  if (ac.lat != null && ac.lng != null) {
    return `${FR24_BASE}/${Number(ac.lat).toFixed(4)},${Number(ac.lng).toFixed(4)}/12`;
  }
  return FR24_BASE;
}

// "5h 58m" / "42m" left on a mute (null once it has run out).
function fmtMuteLeft(until, now) {
  if (!until) return null;
  const t = new Date(until).getTime();
  if (isNaN(t)) return null;
  const mins = Math.round((t - now) / 60000);
  if (mins <= 0) return null;
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Compact "how long ago" for the last-calculation label. 5s, 30s, 3min, 5h…
// `now` is passed in (ms) so the caller can tick it once per second.
function fmtAgo(ts, now) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const secs = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}min ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}min`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Age of the last calculation, in seconds (null if unknown/invalid).
function ageSeconds(ts, now) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, (now - d.getTime()) / 1000);
}

// Freshness color, hue green→yellow→orange→red. Piecewise so the milestones
// land where we want: fresh = green, 2 min = yellow, ~3 min = orange, 5 min = red.
const AGE_WARN_S = 120; // 2 min → yellow
const AGE_MAX_S  = 300; // 5 min → full danger (red)
function ageColors(secs) {
  if (secs == null) return { fg: '#888', bg: 'transparent', border: 'transparent' };
  let hue;
  if (secs <= AGE_WARN_S) {
    hue = 120 - 60 * (secs / AGE_WARN_S);                 // green (120) → yellow (60)
  } else {
    const t = Math.min((secs - AGE_WARN_S) / (AGE_MAX_S - AGE_WARN_S), 1);
    hue = 60 * (1 - t);                                   // yellow (60) → red (0), orange (~40) at 3 min
  }
  return {
    fg: `hsl(${hue}, 80%, 42%)`,
    bg: `hsla(${hue}, 80%, 45%, 0.16)`,
    border: `hsla(${hue}, 75%, 45%, 0.55)`,
  };
}

// High-contrast monochrome icons for the cockpit switches (inherit a solid
// color so they read clearly against both light and dark housings).
function SunIcon({ size = 18, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" stroke={color} strokeWidth="2.2" strokeLinecap="round" fill="none" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="4.3" fill={color} stroke="none" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map(a => {
        const r = (a * Math.PI) / 180;
        return <line key={a} x1={12 + 6.5 * Math.sin(r)} y1={12 - 6.5 * Math.cos(r)} x2={12 + 10 * Math.sin(r)} y2={12 - 10 * Math.cos(r)} />;
      })}
    </svg>
  );
}
function MoonIcon({ size = 18, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'block' }}>
      <path d="M21 13.5A8.5 8.5 0 0 1 10.5 3 7.5 7.5 0 1 0 21 13.5z" />
    </svg>
  );
}
function RadarIcon({ size = 18, color }) {
  // Concentric rings + sweep blip = the radar (SVG) view.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill={color} stroke="none" />
    </svg>
  );
}
function MapIcon({ size = 18, color }) {
  // Location pin = the map view.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'block' }}>
      <path d="M12 2c-3.9 0-7 3.1-7 7 0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
    </svg>
  );
}

function AlertIcon({ size = 18, color }) {
  // Warning triangle + exclamation = alerts only.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M12 3.5 L21.5 20.5 L2.5 20.5 Z" />
      <line x1="12" y1="10" x2="12" y2="14.5" strokeLinecap="round" />
      <circle cx="12" cy="17.3" r="0.6" fill={color} stroke="none" />
    </svg>
  );
}
function AllIcon({ size = 18, color }) {
  // Cluster of blips = all traffic.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'block' }}>
      <circle cx="7" cy="7" r="2.4" /><circle cx="17" cy="7" r="2.4" />
      <circle cx="7" cy="17" r="2.4" /><circle cx="17" cy="17" r="2.4" />
    </svg>
  );
}
// Clock face with a filled "slice of cake" wedge pointing where the traffic is,
// in the SAME angle the compass plots it (angle = relative_bearing − effectiveTrack).
// 0 = up (12 o'clock), increasing clockwise.
function ClockIcon({ angle, size = 28, color }) {
  const R = 10.2;          // wedge radius (just inside the rim)
  const HALF = 26;         // half-width of the slice in degrees → ~52° wedge
  const pt = deg => {
    const t = (deg * Math.PI) / 180;
    return [12 + R * Math.sin(t), 12 - R * Math.cos(t)];
  };
  const [x1, y1] = pt(angle - HALF);
  const [x2, y2] = pt(angle + HALF);
  // M center → first rim point → arc (clockwise, sweep=1) to second → close.
  const slice = `M 12 12 L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block' }}>
      {/* Clock face */}
      <circle cx="12" cy="12" r="11" fill="none" stroke={color} strokeWidth="1" opacity="0.1" />
      {/* 12 / 3 / 6 / 9 tick marks for orientation */}
      {[0, 90, 180, 270].map(d => {
        const t = (d * Math.PI) / 180;
        return <line key={d} x1={12 + 9.4 * Math.sin(t)} y1={12 - 9.4 * Math.cos(t)} x2={12 + 11 * Math.sin(t)} y2={12 - 11 * Math.cos(t)} stroke={color} strokeWidth="1.4" opacity="0.14" />;
      })}
      {/* The slice */}
      <path d={slice} fill={color} />
    </svg>
  );
}

function resolveUsername() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('username')) return params.get('username');
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[parts.length - 1] || null;
}

function resolveScene() {
  return new URLSearchParams(window.location.search).get('scene') || null;
}

// t=<radar token> → the per-pilot secret that comes with every radar deep-link
// (push notification, admin "open TCAS"). The /api/tcas endpoints require it, so
// a username on its own no longer opens someone's live traffic. On the logged-in
// path RootRoute passes the pilot's own token from /api/auth/me/ instead.
function resolveToken() {
  return new URLSearchParams(window.location.search).get('t') || null;
}

// "?t=..." (or "" when there's no token) for endpoints that take no other params.
function tokenQuery(token) {
  return token ? `?t=${encodeURIComponent(token)}` : '';
}

// view_only=1 → we're watching/debugging someone else's TCAS; our phone must NOT
// upload any data (no position/heading, no track_open) — read-only.
function resolveViewOnly() {
  return new URLSearchParams(window.location.search).get('view_only') === '1';
}

// test=<seconds> → we just launched a fly-by, so poll fast for that long (instead
// of the 30 s idle cadence) to catch its approach. A fly-by is configurable and
// can run for minutes, so the window comes from the plan the backend returned;
// the legacy `test=1` keeps the old fixed window.
const TEST_FAST_POLL_MS = 50_000;
function resolveTestWindowMs() {
  const raw = new URLSearchParams(window.location.search).get('test');
  if (!raw) return 0;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 1) return TEST_FAST_POLL_MS;
  // A little margin so the radar is still polling fast as the fly-by ends.
  return (seconds + 15) * 1000;
}

// incident=<id> → replay every scene in that incident's window in sequence
// (fetched from /api/incidents/<id>/scenes/) instead of live polling.
// autoplay=1 → start stepping immediately on load.
function resolveIncident() {
  return new URLSearchParams(window.location.search).get('incident') || null;
}
function resolveAutoplay() {
  return new URLSearchParams(window.location.search).get('autoplay') === '1';
}
// Base step cadence for incident replay; divided by the speed factor.
const REPLAY_STEP_MS = 1000;

// "28.6. 14:36:56" for the frame being replayed. Without this the replay screen
// only showed a scene id and a coarse "39d 8h ago" chip, so two incidents from
// the same day (same traffic, minutes apart) were indistinguishable on screen.
function fmtFrameTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}. ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function TcasScreen({ username: usernameProp, viewOnly: viewOnlyProp, token: tokenProp }) {
  // Username/viewOnly come from props (logged-in user, or a deep-link route);
  // fall back to URL resolution so legacy ?username=... links still work.
  const [username] = useState(() => usernameProp ?? resolveUsername());
  const [scene]    = useState(resolveScene);
  const [viewOnly] = useState(() => viewOnlyProp ?? resolveViewOnly());
  // Radar link token: the pilot's own when logged in, otherwise the one in the URL.
  const [token]    = useState(() => tokenProp ?? resolveToken());
  // Incident replay: when set, we step through `sequence` instead of live polling.
  const [incident]  = useState(resolveIncident);
  const [autoplay]  = useState(resolveAutoplay);
  const [sequence, setSequence]     = useState([]);   // [{id, time_created, alert_level}]
  const [replayIdx, setReplayIdx]   = useState(0);
  const [replaying, setReplaying]   = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(5); // 5× default so replays play through quickly
  // While this is in the future, poll at the fast cadence even when CLEAR (set when
  // the radar is opened from the "Test threat" button so the fly-by is caught live).
  const [fastPollUntil] = useState(() => {
    const windowMs = resolveTestWindowMs();
    return windowMs ? Date.now() + windowMs : 0;
  });
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [dataVersion, setDataVersion] = useState(0); // increments each successful fetch
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false); // false = alerts only, true = all aircraft
  // Tapped threat card → action popup. Held as a key (not the object) so the popup
  // keeps tracking the same contact as new payloads arrive; `selectedRef` holds the
  // last known snapshot so the popup survives a poll where the contact drops out.
  const [selectedKey, setSelectedKey] = useState(null);
  const [muteBusy, setMuteBusy]  = useState(false);
  const [muteErr, setMuteErr]    = useState(null);
  const selectedRef = useRef(null);
  const [northUp] = useState(false); // compass orientation: always track-up (the N-UP toggle was removed)
  const [pollMs, setPollMs] = useState(POLL_IDLE_MS); // active poll cadence (for the small UI indicator)
  const [deviceAlt, setDeviceAlt] = useState(null); // GPS altitude (m), null until the device reports it
  const { theme, toggle: toggleTheme } = useTheme();
  // Live device-compass heading (null until the sensor reports one). Rounded to
  // a whole degree so we only re-render on real change. Drives the o'clock cue.
  const [deviceHeading, setDeviceHeading] = useState(null);
  const gpsRef     = useRef(null);
  const headingRef = useRef(null);
  const showFrame  = useShowPhoneFrame();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  // Show the settings gear only on the pilot's own live radar (logged in, not a
  // read-only spectator deep-link).
  const showSettings = !!user && !viewOnly;
  // Nudge the pilot to set up their keys if either is still missing (dismissable).
  const [missingDismissed, setMissingDismissed] = useState(false);
  const missingData = showSettings && !missingDismissed && (!user.puretrack_key || !user.pushover_user_key);

  useEffect(() => {
    if (username && !viewOnly) {
      fetch(`${API_BASE}/api/tcas/${username}/track_open/${tokenQuery(token)}`, { method: 'POST' })
        .catch(() => {});
    }
  }, [username, viewOnly, token]);

  useEffect(() => {
    if (viewOnly || !navigator.geolocation) return; // read-only: don't even read GPS
    const id = navigator.geolocation.watchPosition(
      pos => {
        gpsRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, alt: pos.coords.altitude };
        if (pos.coords.altitude != null) setDeviceAlt(Math.round(pos.coords.altitude));
      },
      err => console.warn('[gps]', err.message),
      { enableHighAccuracy: true, maximumAge: 2000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [viewOnly]);

  const fetchData = useCallback(async () => {
    if (!username) return null;
    const params = new URLSearchParams();
    if (token) params.set('t', token);
    if (scene) params.set('scene', scene);
    // Read-only: never upload our position/heading (the backend only creates a
    // Position + Scene when lat & lng are present, so omitting them = pure read).
    const pos = viewOnly ? null : gpsRef.current;
    if (pos) { params.set('lat', pos.lat); params.set('lng', pos.lng); }
    if (pos && pos.alt != null) params.set('alt', Math.round(pos.alt));
    if (!viewOnly && headingRef.current != null) params.set('hdg', Math.round(headingRef.current));
    try {
      const r = await fetch(`${API_BASE}/api/tcas/${username}/?${params}`);
      // 403 = missing/invalid link token, which reads very differently from "no
      // data": it's an old push link (sent before tokens existed) or a hand-typed
      // ?username=. Say so instead of blaming the data.
      if (r.status === 403) throw new Error('403');
      if (!r.ok) throw new Error(r.status);
      const json = await r.json();
      setData(json); setError(null);
      setDataVersion(v => v + 1); // bump on every successful fetch → drives the compass blink
      return json.alert_level;
    } catch (e) {
      setError(e.message === '403'
        ? 'Link not valid. Open the radar from a new alert, or log in.'
        : `No data for "${username}"`);
      return null;
    }
  }, [username, scene, viewOnly, token]);

  useEffect(() => {
    if (incident) return; // incident replay drives `data`; skip live polling
    let cancelled = false;
    let timer;
    const loop = async () => {
      const level = await fetchData();
      if (cancelled) return;
      // Fast cadence while any alert is active OR within the post-"Test threat"
      // window; slow idle cadence otherwise.
      const fast = LEVEL[level] || Date.now() < fastPollUntil;
      const delay = fast ? POLL_ALERT_MS : POLL_IDLE_MS;
      setPollMs(delay);
      timer = setTimeout(loop, delay);
    };
    loop(); // immediate first fetch
    return () => { cancelled = true; clearTimeout(timer); };
  }, [fetchData, fastPollUntil, incident]);

  // Incident replay: load the ordered scene list once, then start if autoplay.
  useEffect(() => {
    if (!incident) return;
    fetch(`${API_BASE}/api/incidents/${incident}/scenes/${tokenQuery(token)}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(list => {
        const seq = list || [];
        setSequence(seq);
        setReplayIdx(0);
        setReplaying(autoplay && seq.length > 1);
        if (!seq.length) setError(`No scenes for incident #${incident}`);
      })
      .catch(() => setError(`Failed to load incident #${incident}`));
  }, [incident, autoplay, token]);

  // Render the current replay scene (backend renders a historical scene by id).
  useEffect(() => {
    if (!incident) return;
    const sc = sequence[replayIdx];
    if (!sc) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/tcas/${username}/?scene=${sc.id}${token ? `&t=${encodeURIComponent(token)}` : ''}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(json => { if (cancelled) return; setData(json); setError(null); setDataVersion(v => v + 1); })
      .catch(() => { if (!cancelled) setError(`No data for "${username}"`); });
    return () => { cancelled = true; };
  }, [incident, username, sequence, replayIdx, token]);

  // Fixed-cadence stepping through the sequence; stop at the last scene.
  useEffect(() => {
    if (!incident || !replaying || sequence.length < 2) return;
    const intervalMs = Math.max(150, REPLAY_STEP_MS / replaySpeed);
    const t = setInterval(() => {
      setReplayIdx(prev => {
        if (prev >= sequence.length - 1) { setReplaying(false); return prev; }
        return prev + 1;
      });
    }, intervalMs);
    return () => clearInterval(t);
  }, [incident, replaying, replaySpeed, sequence.length]);

  const replayStep = (dir) => {
    setReplaying(false);
    setReplayIdx(prev => Math.min(sequence.length - 1, Math.max(0, prev + dir)));
  };
  const replayToggle = () => {
    if (replayIdx >= sequence.length - 1) setReplayIdx(0); // restart if at end
    setReplaying(p => !p);
  };

  const C = THEMES[theme] || THEMES.dark;
  const s = makeStyles(C);

  // Letterbox stage + phone "device card" (see useShowPhoneFrame / PHONE_W / PHONE_H).
  // The missing-data prompt is a centered overlay so it's impossible to miss in
  // every state (radar, "No data", loading) for a pilot who hasn't set their keys.
  const frame = (body) => (
    <div style={{ ...s.stage, alignItems: showFrame ? 'center' : 'stretch' }}>
      <div style={{ ...(showFrame ? s.cardWide : s.cardFull), position: 'relative' }}>
        {missingData && (
          <div
            style={{
              position: 'absolute', inset: 0, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 24, background: 'rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ position: 'relative', maxWidth: 320 }}>
              <button
                onClick={() => navigate('/settings')}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                  border: 'none', cursor: 'pointer', textAlign: 'center', padding: '24px 28px',
                  borderRadius: 14, fontWeight: 700, color: '#000', background: LEVEL.WARNING.bg,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)', width: '100%',
                }}
              >
                <span style={{ fontSize: 48, lineHeight: 1 }}>⚠</span>
                <span style={{ fontSize: 17 }}>Missing configuration</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Tap to open Settings</span>
              </button>
              {/* Dismiss without opening Settings (until the radar reloads). */}
              <button
                onClick={() => setMissingDismissed(true)}
                title="Dismiss" aria-label="Dismiss"
                style={{
                  position: 'absolute', top: 8, right: 8, width: 26, height: 26,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', borderRadius: '50%', cursor: 'pointer',
                  background: 'rgba(0,0,0,0.15)', color: '#000', fontSize: 16,
                  fontWeight: 700, lineHeight: 1, padding: 0,
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
        {body}
      </div>
    </div>
  );

  // Menu button for the splash states (no-data / loading / error), where there's no
  // status bar to host the usual one. Floats in the card's top-right corner.
  const cornerGear = showSettings ? (
    <button
      onClick={() => navigate('/menu')} title="Menu" aria-label="Menu"
      style={{ position: 'absolute', top: 6, right: 6, zIndex: 6, height: 40,
               borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface,
               color: C.textStrong, lineHeight: 1, cursor: 'pointer',
               display: 'flex', alignItems: 'center', justifyContent: 'center',
               padding: '0 14px', fontSize: 11, fontWeight: 800, letterSpacing: 1,
               touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
    >
      MENU
    </button>
  ) : null;

  if (!username) return frame(<>{cornerGear}<Splash C={C}>No username. Use ?username=...</Splash></>);
  if (error)     return frame(<>{cornerGear}<Splash C={C}>{error}</Splash></>);
  if (!data)     return frame(<>{cornerGear}<Splash C={C}>Loading…</Splash></>);

  const calculations = data.calculations || [];
  const aircraft = calculations.filter(c => c.user_id === null);
  const hasAlert = c => c.status === 'CRITICAL' || c.status === 'WARNING' || c.status === 'INFO';
  const priOf = st => ({ CRITICAL: 0, WARNING: 1, INFO: 2 }[st] ?? 3);
  // "ALL" view bound: traffic within 50 km. We intentionally do NOT filter on
  // fix age — data_age_s is frozen at scene-creation, so on an old replayed scene
  // (which can be days old) an age cut-off mixes things up while debugging.
  // Missing distance fails the check (excluded). Alerts always show — they already
  // passed the backend's freshness/range/threat gates.
  const ALL_MAX_RANGE_KM = 50;
  const inAllRange = c => c.distance_km != null && c.distance_km <= ALL_MAX_RANGE_KM;
  // Displayed set drives both the cards and the compass markers. The bottom
  // "ALL / ALERTS" switch toggles between all in-range aircraft and alerting ones
  // only. Severity first, then closest; non-alert aircraft last.
  const displayed = (showAll ? aircraft.filter(inAllRange) : aircraft.filter(hasAlert))
    .slice()
    .sort((a, b) => priOf(a.status) - priOf(b.status) || (a.distance_km ?? 0) - (b.distance_km ?? 0));

  const overall = data.alert_level;
  const lvl = LEVEL[overall];

  // Popup target: re-resolved from the freshest payload every poll, falling back to
  // the last snapshot when the contact isn't in the current list (dropped fix, or
  // switched back to ALERTS while looking at a non-alerting aircraft).
  const liveSelected = selectedKey ? displayed.find(a => acKey(a) === selectedKey) : null;
  if (liveSelected) selectedRef.current = liveSelected;
  const selectedAc = selectedKey ? (liveSelected || selectedRef.current) : null;

  const closePopup = () => { setSelectedKey(null); setMuteErr(null); selectedRef.current = null; };

  // Ignore unless CRITICAL ⇄ un-ignore. Only suppresses the push notification for
  // this aircraft — the radar keeps showing it exactly as before.
  const toggleMute = async (ac) => {
    if (!ac || viewOnly || muteBusy) return;
    setMuteBusy(true);
    setMuteErr(null);
    try {
      const res = ac.muted
        ? await unmuteAircraft(username, ac, token)
        : await muteAircraft(username, ac, token);
      // Optimistic: the popup flips immediately, the refetch confirms it.
      selectedRef.current = { ...ac, muted: !ac.muted, muted_until: res?.muted_until || null };
      await fetchData();
    } catch {
      setMuteErr('Failed — try again');
    } finally {
      setMuteBusy(false);
    }
  };

  // Leave an incident replay. Navigating to '/' would NOT work: RootRoute matches
  // '/' regardless of the query string, so TcasScreen never unmounts and its
  // one-shot `incident` state sticks (replay stays on screen). Route to a
  // different component instead — the pilot's own incidents list when logged in,
  // or a hard reload to the live radar for an anonymous spectator deep-link.
  const exitReplay = () => {
    if (user) navigate('/incidents');
    else window.location.assign('/');
  };

  const replayBar = incident ? (
    <div style={{
      position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      padding: '8px 10px', background: 'rgba(0,0,0,0.72)',
      borderTop: `1px solid ${C.border}`, color: C.textStrong,
      fontSize: 12, flexWrap: 'wrap',
    }}>
      <button onClick={() => replayStep(-1)} disabled={replayIdx <= 0}
        style={{ cursor: 'pointer', background: C.surface, color: C.textStrong, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px' }}>◀</button>
      <button onClick={replayToggle} disabled={sequence.length < 2}
        style={{ cursor: 'pointer', background: C.surface, color: C.textStrong, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', fontWeight: 700 }}>
        {replaying ? '❚❚ Pause' : '▶ Play'}
      </button>
      <button onClick={() => replayStep(1)} disabled={replayIdx >= sequence.length - 1}
        style={{ cursor: 'pointer', background: C.surface, color: C.textStrong, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px' }}>▶</button>
      <select value={replaySpeed} onChange={e => setReplaySpeed(Number(e.target.value))}
        style={{ background: C.surface, color: C.textStrong, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 6px' }}>
        <option value={0.5}>0.5×</option>
        <option value={1}>1×</option>
        <option value={2}>2×</option>
        <option value={5}>5×</option>
      </select>
      <span style={{ color: C.textMuted }}>
        {sequence.length ? replayIdx + 1 : 0}/{sequence.length}
      </span>
      {/* Exit the replay → back to the incidents list (or live radar for a spectator). */}
      <button onClick={exitReplay} title="Exit replay"
        style={{ cursor: 'pointer', background: C.surface, color: C.textStrong, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', fontWeight: 700 }}>
        ✕ Exit
      </button>
    </div>
  ) : null;

  return frame(
    <>
      {replayBar}

      {/* Overall status bar */}
      <div style={{
        ...s.statusBar,
        background: lvl ? lvl.bg : C.surface,
      }}>
        <span style={{ ...s.statusText, color: lvl ? '#fff' : C.textStrong }}>{lvl ? lvl.label : 'CLEAR'}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {viewOnly && (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, color: '#fff', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 3, padding: '2px 5px' }}>
              READ ONLY
            </span>
          )}
          <span style={{ ...s.statusUser, color: lvl ? 'rgba(255,255,255,0.75)' : C.textMuted }}>{data.username}{scene ? ` · #${scene}` : (incident && sequence[replayIdx] ? ` · ${fmtFrameTime(sequence[replayIdx].time_created)}` : '')}</span>
          {showSettings && (
            <button
              onClick={() => navigate('/menu')}
              title="Menu" aria-label="Menu"
              // The label is small but the tap target must not be: the padding pads
              // it out to ~44px and the negative margins pull that back, so the bar
              // layout (and the button's position) stay exactly as before. The
              // visible button outline lives on the inner span, so growing the hit
              // area never grows the chip.
              style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1,
                       padding: '11px 14px', margin: '-11px -14px -11px -8px',
                       display: 'flex', alignItems: 'center', color: lvl ? '#fff' : C.textStrong,
                       touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <span style={{ border: `1px solid ${lvl ? 'rgba(255,255,255,0.5)' : C.border}`,
                             borderRadius: 4, padding: '3px 7px', lineHeight: 1,
                             fontSize: 11, fontWeight: 800, letterSpacing: 1 }}>
                MENU
              </span>
            </button>
          )}
          {/* During an incident replay the menu button is hidden (view_only), so give
              the viewer an always-visible way out of the replay. */}
          {incident && (
            <button
              onClick={exitReplay}
              title="Exit replay" aria-label="Exit replay"
              style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', fontSize: 16, fontWeight: 700, color: lvl ? '#fff' : C.textStrong }}
            >
              ✕
            </button>
          )}
        </span>
      </div>

      {/* Aircraft cards (alerts CRITICAL ▸ WARNING ▸ INFO first). Max 3, expandable.
          Set toggled by the ALL/ALERTS switch. Lives in the flexible middle
          region so the compass below stays put. */}
      <div style={s.threats}>
        <>
          <div style={s.cards}>
            {(expanded ? displayed : displayed.slice(0, 2)).map(ac => {
              const lv = LEVEL[ac.status];
              const accent = lv ? lv.bg : C.textMuted; // non-alert aircraft = neutral
              const above = ac.alt_diff_m != null && ac.alt_diff_m > 0;
              const altSign = ac.alt_diff_m != null ? (ac.alt_diff_m > 0 ? '▲' : '▼') : '';
              const altAbs  = ac.alt_diff_m != null ? `${Math.abs(Math.round(ac.alt_diff_m))}m` : '—';
              // Vertical speed: ↑/↓ arrow + m/s, colour-coded like the alt diff.
              const vs      = ac.vertical_speed;
              const vsSign  = vs != null && Math.round(vs) !== 0 ? (vs > 0 ? '↑' : '↓') : '';
              const vsLabel = vs != null ? `${vsSign}${Math.abs(Math.round(vs))} m/s` : null;
              // Clock arrow points exactly where the compass plots this aircraft:
              // angle = relative_bearing − effectiveTrack (0 in north-up, else heading).
              // heading = live device compass if present, else pilot GPS track.
              const heading = deviceHeading != null ? deviceHeading : (data.pilot_position?.hdg ?? null);
              const eff = northUp ? 0 : heading;
              const clockAngle = (ac.relative_bearing != null && (northUp || heading != null))
                ? ((((ac.relative_bearing - (eff || 0)) % 360) + 360) % 360)
                : null;

              const key = acKey(ac);
              const open = () => { setMuteErr(null); setSelectedKey(key); };

              return (
                <div
                  key={key || ac.callsign}
                  // Tap a threat to act on it (ignore / aircraft details).
                  role="button" tabIndex={0}
                  onClick={open}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                  style={{ ...s.card, borderColor: lv ? lv.bg : C.border, flexDirection: 'row', alignItems: 'center',
                           cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >

                  {/* Left: callsign + secondary info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    <span style={{ ...s.callsign, color: accent }}>
                      {ac.muted && <span title="Ignored unless CRITICAL" style={{ marginRight: 4 }}>🔇</span>}
                      {ac.callsign}
                    </span>
                    <span style={s.infoMuted}>{Math.round(ac.speed_kmh || 0)} km/h</span>
                  </div>

                  {/* Right: dist · TTC · alt diff · direction · o'clock (far right).
                      Wide gap between pairs; negative word-spacing keeps each
                      number tight to its unit. */}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', wordSpacing: '-3px' }}>
                    <span style={s.bigVal}>{ac.distance_km_display || '—'}</span>
                    <span style={s.bigVal}>{ac.time_to_collision_display || '—'}</span>
                    {/* Altitude diff stacked with vertical speed as one group */}
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, color: above ? C.altUp : C.altDown }}>
                      <span style={{ ...s.bigVal, color: 'inherit' }}>{altSign}{altAbs}</span>
                      {vsLabel && <span style={s.vsVal}>{vsLabel}</span>}
                    </span>
                    <span style={s.bigVal}>{bearingToCard(ac.relative_bearing)}</span>
                    {clockAngle != null && <ClockIcon angle={clockAngle} size={28} color={C.textStrong} />}
                  </div>
                </div>
              );
            })}
            {/* Invisible placeholders keep room for 2 cards always, so the
                compass below never shifts when 1 or 2 threats are shown. */}
            {!expanded && Array.from({ length: Math.max(0, 2 - displayed.length) }).map((_, i) => (
              <div key={`ph-${i}`} aria-hidden style={{ ...s.card, borderColor: 'transparent', flexDirection: 'row', alignItems: 'center', visibility: 'hidden' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                  <span style={s.callsign}>—</span>
                  <span style={s.infoMuted}>—</span>
                </div>
              </div>
            ))}
          </div>
          {displayed.length > 2 && (
            <button style={s.expandBtn} onClick={() => setExpanded(e => !e)}>
              {expanded ? '▲ Show less' : `▼ Show all (${displayed.length})`}
            </button>
          )}
        </>
      </div>

      {/* Tapped-aircraft actions (ignore / FR24 details) */}
      {selectedAc && (
        <AircraftPopup
          ac={selectedAc} C={C} viewOnly={viewOnly}
          busy={muteBusy} error={muteErr}
          onToggleMute={() => toggleMute(selectedAc)}
          onClose={closePopup}
        />
      )}

      {/* Compass */}
      <Compass aircraft={displayed} pilotPos={data.pilot_position} pilotTrack={data.pilot_position?.hdg} onHeadingChange={h => { headingRef.current = h; setDeviceHeading(Math.round(h)); }} C={C} theme={theme} onToggleTheme={toggleTheme} showAll={showAll} onToggleAircraft={() => setShowAll(a => !a)} northUp={northUp} calcTime={data.time_created} pollMs={pollMs} deviceAlt={deviceAlt != null ? deviceAlt : (data.pilot_position?.alt != null ? Math.round(data.pilot_position.alt) : null)} viewOnly={viewOnly} dataVersion={dataVersion} />

    </>
  );
}

// Actions for one tapped contact. Rendered inside the phone card (like the
// missing-config prompt) so it stays inside the device frame on desktop.
function AircraftPopup({ ac, C, viewOnly, busy, error, onToggleMute, onClose }) {
  const lv = LEVEL[ac.status];
  const accent = lv ? lv.bg : C.textStrong;
  const left = fmtMuteLeft(ac.muted_until, Date.now());
  const rows = [
    ['DIST', ac.distance_km_display || '—'],
    ['ALT', ac.alt_diff_m_display || '—'],
    ['SPD', ac.speed_kmh_display || `${Math.round(ac.speed_kmh || 0)} km/h`],
    ['TTC', ac.time_to_collision_display || '—'],
    ['BRG', `${bearingToCard(ac.relative_bearing)}${ac.relative_clock_pos ? ` · ${ac.relative_clock_pos}` : ''}`],
    ['SRC', ac.source || '—'],
  ];
  const btn = {
    width: '100%', padding: '12px 10px', borderRadius: 8, cursor: busy ? 'default' : 'pointer',
    fontFamily: 'monospace', fontSize: 13, fontWeight: 800, letterSpacing: 1,
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex',
               alignItems: 'center', justifyContent: 'center', padding: 20,
               background: 'rgba(0,0,0,0.6)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', width: '100%', maxWidth: 320, background: C.surface,
                 border: `2px solid ${accent}`, borderRadius: 14, padding: '16px 16px 14px',
                 fontFamily: 'monospace', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
      >
        <button
          onClick={onClose} title="Close" aria-label="Close"
          style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28,
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   border: 'none', borderRadius: '50%', cursor: 'pointer', padding: 0,
                   background: C.bg, color: C.textStrong, fontSize: 15, fontWeight: 700, lineHeight: 1 }}
        >
          ✕
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 30 }}>
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1, color: accent }}>
            {ac.callsign || '(no callsign)'}
          </span>
          {lv && (
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#fff',
                           background: lv.bg, borderRadius: 4, padding: '2px 5px' }}>
              {lv.label}
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, margin: '10px 0 14px' }}>
          {rows.map(([label, value]) => (
            <span key={label} style={{ display: 'contents' }}>
              <span style={{ fontSize: 11, color: C.textMuted }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.textStrong, textAlign: 'right' }}>{value}</span>
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Read-only spectator links must not write, so no ignore button there. */}
          {!viewOnly && (
            <button
              onClick={onToggleMute} disabled={busy}
              style={{ ...btn,
                       background: ac.muted ? LEVEL.INFO.bg : C.bg,
                       color: ac.muted ? '#fff' : C.textStrong,
                       border: `1px solid ${ac.muted ? LEVEL.INFO.bg : C.border}`,
                       opacity: busy ? 0.6 : 1 }}
            >
              {busy ? '…' : ac.muted
                ? `🔔 UN-IGNORE${left ? ` · ${left} LEFT` : ''}`
                : '🔇 IGNORE UNLESS CRITICAL · 6h'}
            </button>
          )}
          <button
            onClick={() => window.open(fr24UrlFor(ac), '_blank', 'noopener,noreferrer')}
            style={{ ...btn, background: C.bg, color: C.textStrong, border: `1px solid ${C.border}` }}
          >
            ✈ AIRCRAFT DETAILS
          </button>
        </div>

        <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: error ? LEVEL.CRITICAL.bg : C.textMuted }}>
          {error || (ac.muted ? 'No push notifications — CRITICAL still alerts' : 'Details open FlightRadar24 in a new tab')}
        </div>
      </div>
    </div>
  );
}

function Compass({ aircraft, pilotPos, pilotTrack, onHeadingChange, C, theme, onToggleTheme, showAll, onToggleAircraft, northUp, calcTime, pollMs, deviceAlt, viewOnly, dataVersion }) {
  // RADAR (SVG) ↔ MAP (Leaflet) view toggle, local to the compass. Default RADAR.
  const [mapMode, setMapMode] = useState(false);
  // Full-bleed compass: the circle spans the full width (no side margin). Extra
  // vertical room above/below holds the heading readout and aircraft labels.
  const W       = 320;        // width reference for the viewBox
  // Tight padding so the circle fills the container (the viewBox becomes wider
  // than it is tall → width-constrained → circle spans the full width). PAD_TOP
  // only needs to clear the heading readout; aircraft labels flip below when
  // they'd otherwise clip (see label positioning in the aircraft map).
  const PAD_TOP = 24;
  const PAD_BOT = 4;
  const R       = W / 2 - 1;  // full-width radius (1px keeps the stroke unclipped)
  const cx      = W / 2;
  const cy      = PAD_TOP + R;
  const H       = cy + R + PAD_BOT;

  const [deviceHeading, setDeviceHeading] = useState(null);
  // 'starting' | 'active' | 'needs-permission' | 'denied'
  const [deviceState, setDeviceState] = useState('starting');

  // Ticking "now" so the last-calculation age re-renders every second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Flash the compass briefly each time a fresh server response lands. We key
  // off dataVersion (bumped on every successful fetch) rather than calcTime,
  // since the server timestamp can repeat between polls. Skips the first value.
  const [refreshTick, setRefreshTick] = useState(0);
  const prevVer = useRef(dataVersion);
  useEffect(() => {
    if (prevVer.current !== dataVersion) {
      prevVer.current = dataVersion;
      setRefreshTick(t => t + 1);
    }
  }, [dataVersion]);

  // On mount: start automatically if no permission prompt needed, otherwise flag for user tap
  useEffect(() => {
    if (viewOnly) {
      // Read-only: we're viewing another pilot — don't use our phone's compass;
      // the display follows the viewed pilot's track instead.
      setDeviceState('denied');
      return;
    }
    if (typeof DeviceOrientationEvent === 'undefined') {
      console.log('[compass] DeviceOrientationEvent not available');
      setDeviceState('denied');
      return;
    }
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS 13+ — can't auto-request, need a user gesture
      console.log('[compass] iOS detected — waiting for user tap to request permission');
      setDeviceState('needs-permission');
    } else {
      // Android / desktop — start immediately
      console.log('[compass] No permission required — activating sensor');
      setDeviceState('active');
    }
  }, [viewOnly]);

  const requestIOSPermission = async () => {
    try {
      const result = await DeviceOrientationEvent.requestPermission();
      console.log('[compass] iOS permission result:', result);
      setDeviceState(result === 'granted' ? 'active' : 'denied');
    } catch (err) {
      console.warn('[compass] iOS permission error:', err);
      setDeviceState('denied');
    }
  };

  useEffect(() => {
    if (deviceState !== 'active') return;
    console.log('[compass] Registering deviceorientation listeners');
    let eventCount = 0;
    const handler = (e) => {
      let heading = null;
      if (e.webkitCompassHeading != null) {
        heading = e.webkitCompassHeading;                         // iOS
      } else if (e.alpha != null && (e.absolute || e.type === 'deviceorientationabsolute')) {
        heading = (360 - e.alpha) % 360;                          // Android absolute
      }
      eventCount++;
      if (eventCount <= 3 || eventCount % 50 === 0) {
        console.log(`[compass] (${e.type}) #${eventCount} — absolute:${e.absolute} alpha:${e.alpha?.toFixed(1)} webkitCompassHeading:${e.webkitCompassHeading?.toFixed(1)} → heading:${heading?.toFixed(1)}`);
      }
      if (heading != null) { setDeviceHeading(heading); onHeadingChange?.(heading); }
      else if (eventCount <= 3) console.warn('[compass] No usable heading in event', e);
    };
    // Android Chrome delivers the absolute compass heading via the separate
    // 'deviceorientationabsolute' event; iOS provides webkitCompassHeading on
    // the plain 'deviceorientation' event. Listen for both.
    window.addEventListener('deviceorientationabsolute', handler, true);
    window.addEventListener('deviceorientation', handler, true);
    return () => {
      window.removeEventListener('deviceorientationabsolute', handler, true);
      window.removeEventListener('deviceorientation', handler, true);
    };
  }, [deviceState]);

  const usingSensor = deviceState === 'active' && deviceHeading != null;
  const track = usingSensor ? deviceHeading : (pilotTrack || 0);
  // `track` falls back to 0, which is a real bearing (north) — the map needs to
  // tell "pointing north" from "no heading at all" so it can skip the own-ship
  // track line entirely, as the radar does.
  const trackKnown = usingSensor || pilotTrack != null;

  // In north-up mode the map doesn't rotate — treat track offset as 0
  const effectiveTrack = northUp ? 0 : track;

  // Fixed 5 km radius — aircraft beyond are pinned to the outer ring
  const maxDist = 5;

  // Find where a ray from (x,y) in direction angleDeg hits the compass circle edge
  const rayToEdge = (x, y, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    const dx = Math.sin(rad);
    const dy = -Math.cos(rad);
    const ax = x - cx, ay = y - cy;
    const b = ax * dx + ay * dy;
    const c = ax * ax + ay * ay - R * R;
    const t = -b + Math.sqrt(Math.max(0, b * b - c));
    return { ex: x + t * dx, ey: y + t * dy };
  };

  // Track-up: subtract pilot track so pilot's heading always points to top
  // North-up: effectiveTrack = 0, no rotation applied
  const toXY = (bearingDeg, distKm) => {
    const angle = ((bearingDeg - effectiveTrack) * Math.PI) / 180;
    const ratio = Math.min(distKm / maxDist, 1);
    return {
      x: cx + ratio * R * Math.sin(angle),
      y: cy - ratio * R * Math.cos(angle),
      clipped: distKm > maxDist,
    };
  };

  const ringDistances = [maxDist / 3, (maxDist * 2) / 3, maxDist];

  // 8-point compass rose: cardinals (N/E/S/W) plus intermediates (NE/SE/SW/NW).
  const CARDINALS = [['N', 0], ['NE', 45], ['E', 90], ['SE', 135], ['S', 180], ['SW', 225], ['W', 270], ['NW', 315]];

  return (
    <>
    {deviceState === 'needs-permission' && (
      <div
        onClick={requestIOSPermission}
        style={{
          position: 'absolute', inset: 0, zIndex: 999,
          background: C.overlay,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16, cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: 48 }}>🧭</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.overlayText, fontFamily: 'monospace' }}>Tap to enable compass</div>
        <div style={{ fontSize: 12, color: C.overlaySub, fontFamily: 'monospace' }}>required once per page load on iOS</div>
      </div>
    )}
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '8px 0 4px', background: C.panel, borderTop: `1px solid ${C.borderSoft}`, overflow: 'hidden' }}>
      <style>{'@keyframes tcasRefreshBlink{0%{opacity:1;transform:scale(1)}25%{opacity:0.3;transform:scale(1.12)}55%{opacity:1;transform:scale(1)}}'}</style>
      {/* SVG wrapper takes the remaining vertical space; the compass scales down
          (preserveAspectRatio meet) to fit it while staying circular, so it never
          overlaps the threat cards above or the bottom bar below. In MAP mode the
          Leaflet map fills the same slot instead of the radar circle. */}
      <div style={{ flex: 1, minHeight: 0 }}>
      {mapMode ? (
        // Same heading source the radar rotates by (live compass, else GPS
        // track). Whole degrees: the map only needs to redraw the arrow when
        // the heading visibly changes, not on every raw sensor reading.
        <RadarMap pilot={pilotPos} aircraft={aircraft} heading={trackKnown ? Math.round(track) : null} C={C} />
      ) : (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: '100%' }}
      >
        {/* Heading readout at 12 o'clock (+ altitude alongside) */}
        <text
          x={cx} y={cy - R - 6}
          textAnchor="middle"
          fill={C.text}
          fontSize={13}
          fontWeight="700"
          fontFamily="monospace"
        >
          {(northUp ? '0° N' : `${Math.round(track) % 360}° ${bearingToCard(track)}`) + (deviceAlt != null ? `  ·  ${deviceAlt} m` : '')}
        </text>

        {/* Outer fill */}
        <circle cx={cx} cy={cy} r={R} fill={C.compassFill} stroke={C.border} strokeWidth={1} />

        {/* Distance rings */}
        {ringDistances.map((d, i) => {
          const r = (d / maxDist) * R;
          return (
            <g key={i} opacity={0.45}>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.hairline} strokeWidth={1} strokeDasharray="3 7" />
              <text x={cx + 4} y={cy - r + 12} fill={C.ringText} fontSize={9} fontFamily="monospace">
                {d < 1 ? `${(d * 1000).toFixed(0)}m` : `${d.toFixed(1)}km`}
              </text>
            </g>
          );
        })}

        {/* Rotating group: cardinal labels follow the compass rotation */}
        <g transform={`rotate(${-effectiveTrack}, ${cx}, ${cy})`}>
          {CARDINALS.map(([lbl, deg]) => {
            const rad = (deg * Math.PI) / 180;
            const lr = R - 9;
            const x = cx + lr * Math.sin(rad);
            const y = cy - lr * Math.cos(rad);
            const inter = lbl.length === 2; // NE/SE/SW/NW — smaller
            return (
              <text key={lbl} x={x} y={y} textAnchor="middle" dominantBaseline="central"
                fill={lbl === 'N' ? C.north : C.cardinal} fontSize={inter ? 9 : 13} fontWeight="700" fontFamily="monospace"
                transform={`rotate(${effectiveTrack}, ${x}, ${y})`}>{lbl}</text>
            );
          })}
        </g>

        {/* Aircraft */}
        {aircraft.map(ac => {
          if (ac.relative_bearing == null || ac.distance_km == null) return null;
          const { x, y } = toXY(ac.relative_bearing, ac.distance_km);
          const col   = STATUS_COLOR[ac.status] || C.faint;
          const bold  = ac.status === 'CRITICAL' || ac.status === 'WARNING';
          const isAlert = STATUS_COLOR[ac.status] != null; // CRITICAL/WARNING/INFO get a ring
          const sz    = bold ? 10 : 6; // half-size of arrow
          // Contrast helpers: a marker outline that's bright on the dark theme and
          // dark on the light theme (so the colored arrow pops against the compass
          // in both), plus a background-colored halo behind the labels.
          const outline = C.textStrong;
          const halo    = C.compassFill;

          // Arrow rotation: aircraft track adjusted for track-up mode (0 in north-up)
          const visualTrack = (ac.track_deg || 0) - effectiveTrack;

          // Alt diff label: ▲/▼ + metres
          const altSign = ac.alt_diff_m != null ? (ac.alt_diff_m > 0 ? '▲' : '▼') : '';
          const altAbs  = ac.alt_diff_m != null ? `${Math.abs(Math.round(ac.alt_diff_m))}m` : '';
          const distLabel = ac.distance_km_display || '';
          // Vertical speed label: ↑/↓ + m/s (blank when unknown)
          const vs      = ac.vertical_speed;
          const vsSign  = vs != null && Math.round(vs) !== 0 ? (vs > 0 ? '↑' : '↓') : '';
          const vsLabel = vs != null ? `${vsSign}${Math.abs(Math.round(vs))} m/s` : '';

          // Label lines stack outward from the marker: distance, alt diff, then
          // vertical speed. Normally above the marker; flipped below when there's
          // no room above (marker near the top edge) so the text never clips off
          // the top. Clamp x so edge markers don't run off the sides either.
          const lineH   = 12;
          const lines   = [distLabel, `${altSign}${altAbs}`, vsLabel].filter(Boolean);
          const labelAbove = (y - sz - 4 - (lines.length - 1) * lineH) >= 12;
          const labelX  = Math.min(Math.max(x, 28), W - 28);
          const lineY   = (i) => labelAbove
            ? (y - sz - 4) - (lines.length - 1 - i) * lineH
            : (y + sz + 14) + i * lineH;

          return (
            <g key={acKey(ac) || ac.callsign}>
              {/* Glow ring for alerts (CRITICAL/WARNING/INFO) */}
              {isAlert && <circle cx={x} cy={y} r={sz + 6} fill="none" stroke={col} strokeWidth={1.5} opacity={0.3} />}

              {/* Track line extending to compass edge */}
              {(() => { const { ex, ey } = rayToEdge(x, y, visualTrack); return (
                <line x1={x} y1={y} x2={ex} y2={ey}
                  stroke={col} strokeWidth={bold ? 1.5 : 1} strokeDasharray="3 4" opacity={0.4} />
              ); })()}

              {/* Arrow triangle pointing in track direction (contrast outline) */}
              <polygon
                points={`0,${-sz * 1.4} ${-sz * 0.7},${sz * 0.8} ${sz * 0.7},${sz * 0.8}`}
                fill={col}
                stroke={outline}
                strokeWidth={1.2}
                strokeLinejoin="round"
                transform={`translate(${x},${y}) rotate(${visualTrack})`}
              />

              {/* Label lines: distance, alt diff, vertical speed (above or below) */}
              {lines.map((txt, i) => (
                <text
                  key={i}
                  x={labelX}
                  y={lineY(i)}
                  textAnchor="middle"
                  fill={col}
                  stroke={halo}
                  strokeWidth={2.5}
                  paintOrder="stroke"
                  strokeLinejoin="round"
                  fontSize={bold ? 11 : 9}
                  fontWeight="700"
                  fontFamily="monospace"
                >
                  {txt}
                </text>
              ))}
            </g>
          );
        })}

        {/* Pilot track line to edge */}
        {(() => { const { ex, ey } = rayToEdge(cx, cy, northUp ? track : 0); return (
          <line x1={cx} y1={cy} x2={ex} y2={ey}
            stroke={C.pilot} strokeWidth={1.5} strokeDasharray="3 4" opacity={0.4} />
        ); })()}

        {/* Pilot — points up in track-up (heading = top), rotates by heading in north-up */}
        <polygon
          points={`${cx},${cy - 11} ${cx - 8},${cy + 7} ${cx + 8},${cy + 7}`}
          fill={C.pilot}
          transform={northUp ? `rotate(${track}, ${cx}, ${cy})` : undefined}
        />
        <polygon
          points={`${cx},${cy - 11} ${cx - 8},${cy + 7} ${cx + 8},${cy + 7}`}
          fill="none"
          stroke={C.pilotOutline}
          strokeWidth={1}
          transform={northUp ? `rotate(${track}, ${cx}, ${cy})` : undefined}
        />
      </svg>
      )}
      </div>
      {/* Last calculation freshness (green→red by age) + heading source */}
      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 12px' }}>
        {(() => {
          const secs = ageSeconds(calcTime, now);
          const ac = ageColors(secs);
          const label = fmtAgo(calcTime, now);
          return (
            // key={refreshTick} remounts the pill on each fresh response so the
            // blink animation restarts — signaling the data just refreshed.
            <span key={refreshTick} style={{
              display: 'inline-block', transformOrigin: 'left center',
              fontSize: 13, fontFamily: 'monospace', fontWeight: 800, letterSpacing: 0.5,
              color: ac.fg, background: ac.bg,
              border: `1.5px solid ${ac.border}`, borderRadius: 10,
              padding: '3px 9px', transition: 'color 0.5s, background 0.5s, border-color 0.5s',
              animation: refreshTick ? 'tcasRefreshBlink 0.5s ease' : undefined,
            }}>
              {label ? `⟳ ${label} ago` : '⟳ —'}
            </span>
          );
        })()}
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, fontSize: 11, fontFamily: 'monospace', color: C.faint }}>
          <span>↻ {Math.round(pollMs / 1000)}s</span>
          <span>Device sensors <span style={{ color: usingSensor ? '#1a9e4b' : C.faint }}>●</span></span>
        </span>
      </div>
    </div>

    {/* Bottom bar — cockpit-style two-position toggle switches. In-flow at the
        bottom of the fixed-height card, so it stays put and never overlaps. */}
    <div style={{
      flexShrink: 0,
      display: 'flex', gap: 0, padding: 0,
      background: C.panel, borderTop: `1px solid ${C.borderSoft}`,
    }}>
      {(() => {
        // Cockpit-style selector: both positions listed, the active one marked
        // with its icon and full brightness; the other dimmed. Taller buttons.
        const ic = C.textStrong;
        const housing = {
          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap: 5, padding: '9px 0 10px 16px', fontFamily: 'monospace', cursor: 'pointer',
          border: 'none', borderRadius: 0, background: C.surface, textAlign: 'left',
        };
        const optBase = { display: 'flex', alignItems: 'center', gap: 7, letterSpacing: 1, color: C.textStrong };
        const optSel = { ...optBase, fontSize: 14, fontWeight: 800, opacity: 1 };
        const optUn  = { ...optBase, fontSize: 12, fontWeight: 700, opacity: 0.32 };
        const slot   = { width: 16, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
        const Switch = (onClick, title, opts, last) => (
          <button onClick={onClick} title={title} style={{ ...housing, ...(last ? null : { borderRight: `1px solid ${C.borderSoft}` }) }}>
            {opts.map(o => (
              <span key={o.label} style={o.selected ? optSel : optUn}>
                <span style={slot}>{o.selected ? o.icon : null}</span>
                {o.label}
              </span>
            ))}
          </button>
        );
        return (
          <>
            {Switch(() => setMapMode(m => !m), 'Toggle radar / map', [
              { label: 'RADAR', selected: !mapMode, icon: <RadarIcon size={13} color={ic} /> },
              { label: 'MAP',   selected: mapMode,  icon: <MapIcon size={13} color={ic} /> },
            ])}
            {Switch(onToggleAircraft, 'Toggle shown aircraft', [
              { label: 'ALERTS', selected: !showAll, icon: <AlertIcon size={13} color={ic} /> },
              { label: 'ALL',    selected: showAll,  icon: <AllIcon size={13} color={ic} /> },
            ])}
            {Switch(onToggleTheme, 'Toggle theme', [
              { label: 'DARK',  selected: theme === 'dark',  icon: <MoonIcon size={13} color={ic} /> },
              { label: 'LIGHT', selected: theme === 'light', icon: <SunIcon size={13} color={ic} /> },
            ], true)}
          </>
        );
      })()}
    </div>
    </>
  );
}

function Splash({ children, C }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.splash, fontFamily: 'monospace', fontSize: 16,
                  // Longer messages (e.g. the invalid-link one) must wrap inside the
                  // card instead of running off both edges.
                  padding: '0 20px', textAlign: 'center' }}>
      {children}
    </div>
  );
}

function makeStyles(C) {
  const cardBase = { background: C.bg, display: 'flex', flexDirection: 'column', fontFamily: 'monospace', userSelect: 'none', position: 'relative', width: '100%' };
  return {
    stage:      { background: C.stage, minHeight: '100dvh', display: 'flex', justifyContent: 'center' },
    // Real device: full-screen. Desktop (mouse/trackpad): clamped, centered device card.
    cardFull:   { ...cardBase, height: '100dvh', overflow: 'hidden' },
    cardWide:   { ...cardBase, maxWidth: PHONE_W, height: '100vh', maxHeight: PHONE_H, overflow: 'hidden', borderRadius: 44, border: `6px solid ${C.bezel}`, boxShadow: `0 0 0 2px ${C.bezelRing}, 0 12px 48px ${C.shadow}` },
    statusBar:  { padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background 0.3s' },
    statusText: { fontSize: 18, fontWeight: 900, letterSpacing: 2 },
    statusUser: { fontSize: 12 },
    // Threat list: holds its content (up to 3 cards) and never shrinks, so the
    // cards are always visible. The compass below is the flexible element that
    // scales to fit the remaining space. Capped + scrollable so the expanded
    // "show all" list can't push the compass off-screen.
    threats:    { flexShrink: 0, minHeight: 0, maxHeight: '52dvh', overflowY: 'auto', display: 'flex', flexDirection: 'column' },
    cards:      { display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 8px' },
    expandBtn:  { margin: '0 8px 6px', padding: '8px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: 'monospace', fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' },
    card:       { background: C.surface, borderRadius: 6, border: '2px solid', padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 3 },
    callsign:   { fontSize: 14, fontWeight: 800, letterSpacing: 1 },
    bigVal:     { fontSize: 18, fontWeight: 700, color: C.textStrong, lineHeight: 1 },
    vsVal:      { fontSize: 11, fontWeight: 700, color: 'inherit', lineHeight: 1 },
    infoMuted:  { fontSize: 11, color: C.textMuted },
    clear:      { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, paddingTop: 60 },
    clearIcon:  { fontSize: 64, color: C.clearIcon },
    clearText:  { fontSize: 20, color: C.clearText, letterSpacing: 2 },
  };
}

// Root "/" route. A deep link (?username=... and/or ?view_only=...) always renders
// the radar with no auth — preserves operator (view_only=1) and push (live) links.
// Otherwise: logged in → own live radar; not logged in → login screen.
function RootRoute() {
  const { user, loading } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const hasDeepLink = params.has('username') || params.has('view_only');

  if (hasDeepLink) {
    // The link carries its own credential (?t=); nothing else is available here.
    return <TcasScreen username={resolveUsername()} viewOnly={resolveViewOnly()} token={resolveToken()} />;
  }
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Own radar: the pilot's token comes from /api/auth/me/, so no token in the URL.
  return <TcasScreen username={user.username} viewOnly={false} token={user.radar_token} />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
            <Route path="/menu" element={<RequireAuth><MenuPage /></RequireAuth>} />
            <Route path="/flyby" element={<RequireAuth><FlybyPage /></RequireAuth>} />
            <Route path="/incidents" element={<RequireAuth><IncidentsPage /></RequireAuth>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/instructions/:topic" element={<InstructionsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
